import {
  useGetTemporalEvents,
  useGetUserTicketsTemporal,
} from '@sunrise-ui/api-client';
import type { Event } from '@sunrise-ui/api/events';
import { TemporalRelation } from '@sunrise-ui/api/events';
import { useAppState } from '@sunrise-ui/primitives';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';

export function useEventCheckInModal() {
  const { appState } = useAppState();
  const [modalVisible, setModalVisible] = useState(false);
  const [activeEventId, setActiveEventId] = useState<string>();

  // Fetch user's events across ALL communities
  const { data: futureEvents } = useGetTemporalEvents({
    temporalRelation: TemporalRelation.Future,
  });

  const { data: tickets } = useGetUserTicketsTemporal({
    relation: TemporalRelation.Future,
  });

  const userTicket = useMemo(
    () => tickets?.find((ticket) => ticket.event_id === activeEventId),
    [tickets, activeEventId],
  );

  const isEventInActiveWindow = useCallback((event: Event): boolean => {
    if (!event.start_date || !event.scheduled_end_date) return false;

    const now = DateTime.now();
    const startDate = DateTime.fromISO(event.start_date);
    const endDate = DateTime.fromISO(event.scheduled_end_date);

    // Check if within 30 mins before start to 30 mins after end
    const thirtyMinutesBeforeStart = startDate.minus({ minutes: 30 });
    const thirtyMinutesAfterEnd = endDate.plus({ minutes: 30 });

    return now >= thirtyMinutesBeforeStart && now <= thirtyMinutesAfterEnd;
  }, []);

  const findActiveEvent = useCallback(() => {
    if (!futureEvents || futureEvents.length === 0) return;
    for (const event of futureEvents) {
      if (isEventInActiveWindow(event)) {
        return event.id;
      }
    }
  }, [futureEvents, isEventInActiveWindow]);

  useEffect(() => {
    const eventId = findActiveEvent();
    setActiveEventId(eventId);
  }, [findActiveEvent]);

  // Show modal when there's an active event and user has a ticket and app is foregrounded
  useEffect(() => {
    if (activeEventId && userTicket && appState === 'active') {
      setModalVisible(true);
    } else {
      setModalVisible(false);
    }
  }, [activeEventId, userTicket, appState]);

  const hideModal = () => {
    setModalVisible(false);
  };

  return {
    modalVisible,
    hideModal,
    activeEventId,
    userTicket,
  };
}
